import React, { useCallback, useEffect, useRef, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import axios from "axios";
import "./ChatUI.css";
import { throttle } from "../../../utils/throttle";
import swal from "sweetalert";
import { TELEGRAM_API_BASE_URL } from "../../../utils/constant";
import {
  config,
  getCompleteDomain,
  getServerEnvironment,
} from "../../../utils/helper";

const ChatUI = ({ toggleRightBar }) => {
  // Constants
  const MESSAGE_BATCH_SIZE = 20;
  const USERNAME = "ajay"; // This should ideally come from authentication
  const MAX_LOCALSTORAGE_SIZE = 4 * 1024 * 1024; // 4MB (leaving some room under 5MB limit)

  const { Url, WSUrl } = config(getServerEnvironment(getCompleteDomain()));

  // Chat state
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState({});
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Pagination state
  const [loadingMore, setLoadingMore] = useState(false);
  const [messageOffsets, setMessageOffsets] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Refs
  const stompClientRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const messageIdsRef = useRef(new Set()); // Track message IDs to prevent duplicates
  const [pageNumbers, setPageNumbers] = useState({}); // Track current page for each group
  const [hasMoreMessages, setHasMoreMessages] = useState({}); // Track if more messages are available
  const [isNearBottom, setIsNearBottom] = useState(true);

  // Utility functions
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getFileIcon = (fileNameOrExtension) => {
    if (!fileNameOrExtension) return "📄";
    const extension = fileNameOrExtension.includes(".")
      ? fileNameOrExtension.split(".").pop().toLowerCase()
      : fileNameOrExtension.toLowerCase();

    const icons = {
      jpg: "🖼",
      jpeg: "🖼",
      png: "🖼",
      gif: "🖼",
      webp: "🖼",
      pdf: "📄",
      doc: "📝",
      docx: "📝",
      txt: "📋",
      rtf: "📝",
      xls: "📊",
      xlsx: "📊",
      csv: "📊",
      ods: "📊",
      ppt: "📑",
      pptx: "📑",
      odp: "📑",
      zip: "🗜",
      rar: "🗜",
      "7z": "🗜",
      tar: "🗜",
      default: "📁",
    };
    return icons[extension] || icons.default;
  };

  const handleDownload = (message) => {
    if (!message.fileData) return;
    try {
      const link = document.createElement("a");
      link.href = message.fileData;
      link.download =
        message.fileName || `document.${message.fileExtension || "bin"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  // Check if data is too large for localStorage
  const isDataTooLarge = (data) => {
    try {
      const jsonString = JSON.stringify(data);
      return jsonString.length > MAX_LOCALSTORAGE_SIZE;
    } catch (e) {
      console.error("Error checking data size:", e);
      return true;
    }
  };

  // Safe localStorage setter with size checking
  const safeSetLocalStorage = (key, data) => {
    try {
      if (isDataTooLarge(data)) {
        console.warn(
          `Data for ${key} is too large for localStorage, skipping save`
        );
        return false;
      }
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error(Error`saving ${key} to localStorage:`, error);
      return false;
    }
  };

  // Load initial state from localStorage
  const loadInitialState = () => {
    try {
      const savedGroups = localStorage.getItem("chat_groups");
      const savedSelectedGroup = localStorage.getItem("selected_group");
      const savedGroupMessages = localStorage.getItem("group_messages");
      const savedUnreadCounts = localStorage.getItem("unread_counts");

      if (savedGroups) setGroups(JSON.parse(savedGroups));
      if (savedSelectedGroup) setSelectedGroup(JSON.parse(savedSelectedGroup));
      if (savedGroupMessages) setGroupMessages(JSON.parse(savedGroupMessages));
      if (savedUnreadCounts) setUnreadCounts(JSON.parse(savedUnreadCounts));
    } catch (error) {
      console.error("Error loading state from localStorage:", error);
      // Clear corrupted data
      localStorage.removeItem("chat_groups");
      localStorage.removeItem("selected_group");
      localStorage.removeItem("group_messages");
      localStorage.removeItem("unread_counts");
    }
  };

  // Generate a unique ID for a message based on its content and timestamp
  const getMessageId = (message) => {
    const timestamp = message.messageDate
      ? new Date(message.messageDate).getTime()
      : Date.now();
    const contentHash = btoa(
      encodeURIComponent(
        (message.msgText || "") +
          (message.photo || "") +
          (message.fileData || "") +
          (message.caption || "")
      )
    ).slice(0, 20);
    return `${timestamp}-${contentHash}`;
  };

  // Handle incoming WebSocket messages
  const handleIncomingMessage = useCallback(
    (receivedMessage) => {
      setShouldAutoScroll(isNearBottom);

      const messageDate = receivedMessage.messageDate
        ? new Date(receivedMessage.messageDate)
        : new Date();

      const message = {
        chatId: receivedMessage.chatId,
        userName: receivedMessage.userName || "Admin",
        messageDate: messageDate,
        msgText: receivedMessage.msgText || receivedMessage.adminReplyMsg || "",
        msgType: receivedMessage.msgType || receivedMessage.fileType || "text",
        sender: receivedMessage.senderType || "admin",
        caption: receivedMessage.caption,
        fileName: receivedMessage.fileName,
        fileExtension: receivedMessage.fileName?.split(".").pop(),
        contentType: receivedMessage.contentType,
      };

      const messageId = getMessageId(message);
      if (messageIdsRef.current.has(messageId)) return;
      messageIdsRef.current.add(messageId);

      if (receivedMessage.photo) {
        message.photo = receivedMessage.photo;
        message.msgType = "photo";
      }

      if (receivedMessage.fileData) {
        message.fileData = receivedMessage.fileData;
        message.msgType = "document";
      }

      // Update state with sorted messages
      setGroupMessages((prev) => {
        const existingMessages = prev[receivedMessage.chatId] || [];
        const allMessages = [...existingMessages, message].sort(
          (a, b) => new Date(a.messageDate) - new Date(b.messageDate)
        );
        return {
          ...prev,
          [receivedMessage.chatId]: allMessages,
        };
      });

      // Update unread counts if not the current chat
      if (
        !selectedGroup ||
        receivedMessage.chatId !== selectedGroup.chatId
      ) {
        setUnreadCounts((prev) => ({
          ...prev,
          [receivedMessage.chatId]: (prev[receivedMessage.chatId] || 0) + 1,
        }));

        // Show notification if permission granted and window not focused
        if (Notification.permission === "granted" && !document.hasFocus()) {
          try {
            const notificationBody =
              message.msgType === "text"
                ? message.msgText
                : message.msgType === "photo"
                ? "📷 Photo"
                : "📄 Document";

            const notification = new Notification(
              `New message from ${message.userName}`,
              {
                body: notificationBody,
                icon: "/path/to/notification-icon.png", // Add your icon path
                tag: `message-${messageId}`, // Group similar notifications
              }
            );

            // Handle notification click
            notification.onclick = () => {
              window.focus();
              // Optionally switch to the chat where the message came from
              const messageGroup = groups.find(
                (g) => g.chatId === receivedMessage.chatId
              );
              if (messageGroup) {
                setSelectedGroup(messageGroup);
              }
            };
          } catch (error) {
            console.error("Error showing notification:", error);
          }
        }
      }
    },
    [selectedGroup, isNearBottom, groups]
  );
  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        console.log("Notification permission:", permission);
      });
    }
  }, []);

  // Save states to localStorage when they change
  useEffect(() => {
    if (initialLoadComplete) {
      safeSetLocalStorage("chat_groups", groups);
    }
  }, [groups, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      safeSetLocalStorage("selected_group", selectedGroup);
    }
  }, [selectedGroup, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      // Only save the most recent messages to localStorage to avoid quota issues
      const messagesToSave = Object.keys(groupMessages).reduce(
        (acc, groupId) => {
          acc[groupId] = groupMessages[groupId].slice(-50); // Save only last 50 messages per group
          return acc;
        },
        {}
      );
      safeSetLocalStorage("group_messages", messagesToSave);
    }
  }, [groupMessages, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      safeSetLocalStorage("unread_counts", unreadCounts);
    }
  }, [unreadCounts, initialLoadComplete]);

  const loadGroups = async () => {
    try {
      const response = await axios.get(
        `${TELEGRAM_API_BASE_URL}/merchant-group/getAllTelegramGroup`
      );
      if (response.data.status === "success") {
        setGroups(response.data.data);

        // Initialize pagination states
        const initialPageNumbers = {};
        const initialHasMore = {};

        response.data.data.forEach((group) => {
          initialPageNumbers[group.chatId] = 1;
          initialHasMore[group.chatId] = true;

          // Initialize empty messages array if not exists
          if (!groupMessages[group.chatId]) {
            setGroupMessages((prev) => ({
              ...prev,
              [group.chatId]: [],
            }));
          }
        });

        setPageNumbers((prev) => ({ ...prev, ...initialPageNumbers }));
        setHasMoreMessages((prev) => ({ ...prev, ...initialHasMore }));
        setInitialLoadComplete(true);
      }
    } catch (error) {
      console.error("Error loading groups:", error);
    }
  };
  const fetchHistoricalMessages = async (groupId, pageNumber = 1) => {
    if (!groupId || pageNumber < 1) return;

    setShouldAutoScroll(false);

    try {
      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;

      const response = await axios.get(
        `${TELEGRAM_API_BASE_URL}/merchant-group/getStoreChatLimitData?chatId=${groupId}&pageNumber=${pageNumber}`
      );

      const responseData = response.data?.data;
      if (!responseData?.messages?.length) {
        setHasMoreMessages((prev) => ({ ...prev, [groupId]: false }));
        return;
      }

      const messages = processMessages(responseData.messages, groupId);

      // Update state with sorted messages
      setGroupMessages((prev) => {
        const existingMessages = prev[groupId] || [];
        const allMessages = [...messages, ...existingMessages].sort(
          (a, b) => new Date(a.messageDate) - new Date(b.messageDate)
        );
        return {
          ...prev,
          [groupId]: allMessages,
        };
      });

      if (container) {
        requestAnimationFrame(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - previousScrollHeight;
        });
      }

      setPageNumbers((prev) => ({ ...prev, [groupId]: pageNumber + 1 }));
      setHasMoreMessages((prev) => ({
        ...prev,
        [groupId]:
          responseData.hasMore ||
          (responseData.totalPages && pageNumber < responseData.totalPages),
      }));
    } catch (error) {
      console.error("Error fetching paginated messages:", error);
      setHasMoreMessages((prev) => ({ ...prev, [groupId]: false }));

      swal({
        title: "Error",
        text: "Failed to connect",
        icon: "error",
      });
    } finally {
      setLoadingMore(false);
    }
  };
  // Add this utility function to convert date arrays to Date objects
  const arrayToDate = (arr) => {
    if (!Array.isArray(arr) || arr.length < 3) {
      return new Date();
    }

    const [year, month, day, hour = 0, minute = 0, second = 0] = arr;

    return new Date(year, month - 1, day, hour, minute, second);
  };

  // Process messages function (extracted from your existing code)
  const processMessages = (messages, groupId) => {
    return messages.map((chat) => {
      const message = {
        chatId: groupId,
        userName: chat.userName,
        userId: chat.userId,
        messageDate: arrayToDate(chat.messageDate),
        msgType: chat.msgType,
        caption: chat.caption,
        sender:
          chat.senderType || (chat.group === "admin" ? "admin" : "merchant"),
        senderType: chat.senderType,
        msgId: chat.msgId,
        id: chat.id,
      };

      // Handle different message types
      if (chat.msgType === "text") {
        message.msgText = chat.msgText;
      } else if (chat.msgType === "photo") {
        message.photo = chat.photo;
        if (chat.senderType === "admin") {
          message.fileData = chat.fileData;
          message.contentType = chat.contentType;
          message.fileName = chat.fileName;
        }
      } else if (chat.msgType === "document") {
        message.fileData = chat.fileData;
        message.fileName = chat.fileName;
        message.fileExtension = chat.fileName?.split(".").pop();
        message.fileSize = chat.fileSize;
        message.contentType = chat.contentType || chat.fileType;
      }

      return message;
    });
  };
  const loadMessages = async (groupId) => {
    if (!groupId || isLoading) return;

    setIsLoading(true);
    try {
      // Load first page of historical messages
      await fetchHistoricalMessages(groupId, 1);
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || loadingMore || isLoading || !selectedGroup) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // pixels from top

    // If user scrolls near top, load more messages
    if (
      scrollTop < threshold &&
      hasMoreMessages[selectedGroup.chatId] !== false
    ) {
      setShouldAutoScroll(false); // Disable auto-scroll when loading old messages
      setLoadingMore(true);
      fetchHistoricalMessages(
        selectedGroup.chatId,
        pageNumbers[selectedGroup.chatId] || 1
      );
    }
  }, [selectedGroup, loadingMore, isLoading, hasMoreMessages, pageNumbers]);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission().then((permission) => {
        console.log("Notification permission:", permission);
      });
    }

    loadInitialState();
    loadGroups();

    const socket = new SockJS(`${WSUrl}`);
    const client = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: () => {
        console.log("Connected to WebSocket");
        setIsConnected(true);
        stompClientRef.current = client;

        // Subscribe to merchant group messages
        client.subscribe("/topic/merchant-group", (message) => {
          try {
            const receivedMessage = JSON.parse(message.body);
            console.log(message.body);
            handleIncomingMessage(receivedMessage);
          } catch (error) {
            console.error("Error parsing merchant message:", error);
          }
        });

        // Subscribe to admin group messages
        client.subscribe("/topic/admin-group", (message) => {
          try {
            console.log();
            const receivedMessage = JSON.parse(message.body);
            console.log(message.body);
            handleIncomingMessage(receivedMessage);
          } catch (error) {
            console.error("Error parsing admin message:", error);
          }
        });
      },

      onDisconnect: () => {
        console.warn("WebSocket Disconnected");
        setIsConnected(false);
        stompClientRef.current = null;
      },

      onStompError: (frame) => {
        console.error("STOMP error:", frame);
      },

      onWebSocketClose: () => {
        console.warn("WebSocket closed, trying to reconnect...");
        setIsConnected(false);
      },
    });

    client.activate();

    return () => {
      // Don't deactivate here to allow reconnect
    };
  }, [handleIncomingMessage]);

  // Load messages when a group is selected
  useEffect(() => {
    if (selectedGroup) {
      setShouldAutoScroll(true); // Enable auto-scroll when group changes

      // Reset unread count
      setUnreadCounts((prev) => ({
        ...prev,
        [selectedGroup.chatId]: 0,
      }));

      // Load messages if needed
      const hasExistingMessages =
        groupMessages[selectedGroup.chatId]?.length > 0;
      if (!hasExistingMessages) {
        loadMessages(selectedGroup.chatId);
      }
    }
  }, [selectedGroup]);

  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScrollWithThrottle = throttle(handleScroll, 200);
    container.addEventListener("scroll", handleScrollWithThrottle);

    return () => {
      container.removeEventListener("scroll", handleScrollWithThrottle);
    };
  }, [handleScroll]);

  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!messagesContainerRef.current || !selectedGroup) return;

    const container = messagesContainerRef.current;

    if (shouldAutoScroll) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [groupMessages, selectedGroup, shouldAutoScroll]);

  const handleFileUpload = async (file, type) => {
    if (!selectedGroup || !file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("chatId", selectedGroup.chatId);
    formData.append("username", USERNAME);
    formData.append("caption", "Attachment");

    try {
      const endpoint =
        type === "image" ? "merchantChatSendPhoto" : "merchantChatSendDocument";

      const response = await axios.post(
        `${TELEGRAM_API_BASE_URL}/merchant-group/${endpoint}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.status === "success") {
        // Do NOT update UI manually here.
        // Wait for WebSocket push to update UI through handleIncomingMessage
        console.log("Upload successful, waiting for WebSocket update...");
      }
    } catch (error) {
      console.error(Error`uploading ${type}`, error);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedGroup) return;

    setShouldAutoScroll(true); // Enable auto-scroll when sending
    try {
      await axios.post(
        `${TELEGRAM_API_BASE_URL}/merchant-group/sendMerchantMessage`,
        {
          chatId: selectedGroup.chatId,
          message: messageInput,
          username: USERNAME,
        }
      );
      setMessageInput("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const filteredGroups = groups.filter(
    (group) =>
      group.groupName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (group.lastMessage &&
        group.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const currentGroupMessages = selectedGroup
    ? groupMessages[selectedGroup.chatId] || []
    : [];

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-start al ign-items-center">
        <button
          type="button"
          className="btn-close m-3"
          onClick={toggleRightBar}
        ></button>
        <div className="fs-2 text-dark fw-bold">Chat</div>
      </div>
      <div className="chat-app p-4">
        {/* Left sidebar */}
        <div className="chat-sidebar border border-gray-300">
          <div className="search-bar">
            <div className="search-container">
              <svg
                className="search-icon"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
              </svg>
              <input
                type="text"
                placeholder="Search or start new chat"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="chat-list">
            {filteredGroups.map((group) => {
              const lastMessage =
                groupMessages[group.chatId]?.[
                  groupMessages[group.chatId]?.length - 1
                ];
              return (
                <div
                  key={group.id}
                  className={`chat-item  ${
                    selectedGroup?.id === group.id ? "selected" : ""
                  }`}
                  onClick={() => setSelectedGroup(group)}
                >
                  <div className="chat-avatar">
                    {group.groupName.charAt(0).toUpperCase()}
                  </div>
                  <div className="chat-info d-flex  flex-column align-items-start">
                    <div className="chat-name-time d-flex align-items-center justify-content-between w-100">
                      <span className="chat-name">{group.groupName}</span>
                      <span className="chat-time">
                        {lastMessage?.messageDate
                          ? new Date(
                              lastMessage.messageDate
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </span>
                    </div>
                    <div className="last-message">
                      {lastMessage?.userName === USERNAME
                        ? `You: ${lastMessage.msgText || ""}`
                        : lastMessage?.msgText || "No messages yet"}
                    </div>
                  </div>
                  {unreadCounts[group.chatId] > 0 && (
                    <div className="unread-badge">
                      {unreadCounts[group.chatId]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right side - Chat area */}
        {selectedGroup ? (
          <div className="chat-area gap-2">
            <div className="chat-header-info py-2 px-4">
              <div className="chat-avatar">
                {selectedGroup.groupName.charAt(0).toUpperCase()}
              </div>
              <div className="chat-title d-flex align-items-center justify-content-between ">
                <div className="chat-name text-black">
                  {selectedGroup.groupName}
                </div>
                <div className="chat-status px-4">
                  {isConnected ? (
                    <span className="text-green font-bold">Online</span>
                  ) : (
                    <span className="text-red font-bold">Offline</span>
                  )}
                </div>
              </div>
            </div>

            <div className="messages-container" ref={messagesContainerRef}>
              {loadingMore && (
                <div className="loading-more">
                  <div className="spinner"></div>
                  Loading older messages...
                </div>
              )}

              {currentGroupMessages.map((msg, index) => {
                const messageClass =
                  msg.sender === "merchant"
                    ? "merchant-message"
                    : msg.sender === "admin"
                    ? "admin-message"
                    : msg.userName === USERNAME
                    ? "outgoing"
                    : "incoming";

                return (
                  <div key={index} className={`message ${messageClass}`}>
                    {" "}
                    {/* Corrected template literal */}
                    <div className="message-content d-flex flex-column align-items-start">
                      {msg.userName !== USERNAME && (
                        <div className="message-sender">
                          {msg.userName}
                          {msg.sender === "merchant" && " (Merchant)"}
                          {msg.sender === "admin" && " (Admin)"}
                        </div>
                      )}
                      {msg.msgText && (
                        <div className="message-text text-black">
                          {msg.msgText}
                        </div>
                      )}
                      {msg.msgType === "photo" && (
                        <img
                          src={msg.photo}
                          alt={msg.caption || "Image attachment"}
                          className="message-image"
                        />
                      )}

                      {msg.msgType === "document" && (
                        <div
                          className="document-message"
                          onClick={() => handleDownload(msg)}
                        >
                          <div className="file-icon">
                            {getFileIcon(msg.fileExtension)}
                          </div>
                          <div className="file-info">
                            <div className="file-name">
                              {msg.fileName || "Document"}
                            </div>
                            <div className="file-meta">
                              <span>{formatFileSize(msg.fileSize)}</span>
                              <span>
                                {msg.fileExtension?.toUpperCase() || "FILE"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {msg.caption && (
                        <div className="message-caption text-black">
                          {msg.caption}
                        </div>
                      )}
                    </div>
                    <div className="message-time">
                      {new Date(msg.messageDate).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="input-area">
              <div className="attachment-menu-container">
                <button
                  className="attachment-button"
                  onClick={() => setAttachmentMenuOpen(!attachmentMenuOpen)}
                >
                  +
                </button>
                {attachmentMenuOpen && (
                  <div className="attachment-menu">
                    <div
                      className="attachment-option"
                      onClick={() => {
                        imageInputRef.current.click();
                        setAttachmentMenuOpen(false);
                      }}
                    >
                      <span className="icon">📷</span> Photo
                    </div>
                    <div
                      className="attachment-option"
                      onClick={() => {
                        fileInputRef.current.click();
                        setAttachmentMenuOpen(false);
                      }}
                    >
                      <span className="icon">📄</span> Document
                    </div>
                  </div>
                )}
              </div>
              <input
                type="text"
                className="message-input"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type a message"
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                disabled={!isConnected}
              />
              <button
                className="send-button"
                onClick={sendMessage}
                disabled={!messageInput.trim() || !isConnected}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                >
                  <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path>
                </svg>
              </button>

              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileUpload(e.target.files[0], "image");
                  }
                  e.target.value = ""; // Clear the input value after selection
                }}
              />
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,.xlsx"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileUpload(e.target.files[0], "document");
                  }
                  e.target.value = ""; // Clear the input value after selection
                }}
              />
            </div>
          </div>
        ) : (
          <div className="empty-chat">
            <div className="empty-content">
              <div className="empty-icon">💬</div>
              <h3>WhatsApp Web</h3>
              <p>Select a chat to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatUI;
